import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { orderByDate } from "../../utils/sortFunctions";
import { disciplineDecisions } from "../../utils/discipline";
import { FiExternalLink } from "react-icons/fi";
import SortControls from "../SortControls/SortControls";
import PillSortButton from "../PillInput/PillSortButton";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import { FaFilter } from "react-icons/fa";
import PillInput from "../PillInput/PillInput";
import AutoCompleteInput from "../AutoCompleteInput/AutoCompleteInput";

const consolidatedList = orderByDate(
  [...disciplineDecisions].map((entry) => {
    entry.sortDate = entry.decisionDate ? entry.decisionDate : "";
    return entry;
  }),
  "sortDate"
);

const DisciplineDecisionsTable = ({ ...props }) => {
  const isMobile = useMediaQuery({ maxWidth: 990 });
  const uniqueLicensees = [
    "All",
    ...new Set(disciplineDecisions.map((decision) => decision.vb)),
  ];
  const [selectedLicensee, setSelectedLicensee] = useState(null);
  const [searchOrderBy, setSearchOrderBy] = useState("sortDate");
  const [searchOrderDirection, setSearchOrderDirection] = useState("desc");
  const [filteredList, setFilteredList] = useState(consolidatedList);
  const [modalOpen, toggleModal] = useState(false);

  const applySorting = () => {
    const sorted = [...filteredList].sort((a, b) => {
      if (searchOrderBy === "vb") {
        return searchOrderDirection === "asc"
          ? a.vb.localeCompare(b.vb)
          : b.vb.localeCompare(a.vb);
      } else if (searchOrderBy === "sortDate") {
        return searchOrderDirection === "asc"
          ? new Date(a.sortDate) - new Date(b.sortDate)
          : new Date(b.sortDate) - new Date(a.sortDate);
      }
      return 0;
    });
    setFilteredList(sorted);
  };

  const applyFilters = (licenseeFilter) => {
    let filtered = consolidatedList;
    if (licenseeFilter !== "All") {
      setSelectedLicensee(licenseeFilter);
      filtered = filtered.filter((item) => item.vb === licenseeFilter);
    } else {
      setSelectedLicensee(null);
    }
    setFilteredList(filtered);
  };

  const setListOrder = (filterId) => {
    if (filterId === searchOrderBy && searchOrderDirection === "asc") {
      setSearchOrderDirection("desc");
    } else {
      setSearchOrderBy(filterId);
      setSearchOrderDirection("asc");
    }
  };

  useEffect(() => {
    applySorting();
  }, [searchOrderBy, searchOrderDirection]);

  return (
    <>
      <div className="flexContainer mobileCol justifyBetween alignEnd">
        <SortControls>
          <PillSortButton
            id="sortDate"
            label="Decision Date"
            active={searchOrderBy === "sortDate"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("sortDate")}
          />
          <PillSortButton
            id="vb"
            label="Licensee"
            active={searchOrderBy === "vb"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("vb")}
          />
        </SortControls>

        <Button
          id="filterBtn"
          icon={<FaFilter />}
          size="sm"
          clickEvt={() => toggleModal(!modalOpen)}
          text="Search Result"
        />
      </div>

      {modalOpen && (
        <Modal closeEvt={() => toggleModal(!modalOpen)}>
          <div>
            <h2>Filter Options</h2>
            <div>
              <div style={{ marginBottom: `.5rem` }}>
                <span className="bold">Licensee</span>
              </div>
              <AutoCompleteInput
                options={uniqueLicensees}
                placeholder="Search Licensee"
                value={selectedLicensee || ""}
                onChange={(value) => applyFilters(value)}
              />
            </div>
          </div>
        </Modal>
      )}

      <div className="overflowContainer">
        {!isMobile ? (
          <table>
            <thead>
              <tr>
                <th>Licensee</th>
                <th>Decision Date</th>
                <th>Decision</th>
                <th>Outcome</th>
              </tr>
            </thead>

            <tbody>
              {filteredList.map((row, i) => (
                <tr key={i} className="topAlign">
                  <td>
                    <a href={row.link} title={row.vb} className="linkHeader">
                      <FiExternalLink
                        style={{
                          verticalAlign: "center",
                          marginRight: ".5rem",
                          fontSize: `1.5rem`,
                        }}
                      />
                      {row.vb}
                    </a>
                  </td>
                  <td>{row.decisionDate}</td>
                  <td style={{ minWidth: `24rem` }}>{row.decisionTxt}</td>
                  <td style={{ minWidth: `24rem` }}>{row.outcomeTxt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            {filteredList.map((row, i) => (
              <div key={i} className="tile">
                <div>
                  <span className="label">Licensee: </span>
                  <a href={row.link} title={row.vb} className="linkHeader">
                    <FiExternalLink
                      style={{
                        verticalAlign: "center",
                        marginRight: ".5rem",
                        fontSize: `1.5rem`,
                      }}
                    />
                    {row.vb}
                  </a>
                </div>

                <div>
                  <span className="label">Decision Date: </span>
                  <span>{row.decisionDate}</span>
                </div>

                <div>
                  <span className="label">Decision: </span>
                  <span>{row.decisionTxt}</span>
                </div>

                <div>
                  <span className="label">Outcome: </span>
                  <span>{row.outcomeTxt}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
};

export default DisciplineDecisionsTable;
